import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

interface Result<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches a REST list endpoint and returns *every* row, transparently walking
 * DRF's `next` page links until exhausted. Also handles endpoints that return
 * a bare array (no pagination envelope).
 *
 * Uses `?page_size=500` on the first request so most lists come back in one
 * round-trip (backend caps at 1000 via `StandardPagination`). Falls back to
 * following `next` links if the server ignores the param or the list is
 * bigger than one page.
 */
export default function usePaginatedResource<T>(
  url: string,
  params?: Record<string, unknown>
): Result<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const all: T[] = [];

        // First page — request a large page_size so most endpoints come back
        // in a single round-trip.
        let res = await api.get(url, {
          params: { ...(params ?? {}), page_size: 500 },
        });

        // Walk pages until we run out of `next` links (or the payload isn't
        // paginated at all).
        while (!cancelled) {
          const payload = res.data;
          if (Array.isArray(payload)) {
            all.push(...(payload as T[]));
            break;
          }
          if (payload && Array.isArray(payload.results)) {
            all.push(...(payload.results as T[]));
            if (!payload.next) break;
            // DRF returns an absolute URL — strip the origin so axios uses
            // our configured baseURL + JWT interceptor. The full path (with
            // ?page=N&page_size=…) becomes the new request.
            let nextPath: string;
            try {
              const u = new URL(payload.next);
              nextPath = u.pathname + u.search;
              // Axios baseURL already ends in `/api`; strip that prefix so we
              // don't double it up.
              const base = new URL(api.defaults.baseURL || '', u.origin);
              if (nextPath.startsWith(base.pathname)) {
                nextPath = nextPath.slice(base.pathname.length) || '/';
              }
            } catch {
              // If parsing fails, treat `next` as-is and let axios sort it.
              nextPath = payload.next as string;
            }
            res = await api.get(nextPath);
          } else {
            break;
          }
        }

        if (!cancelled) setData(all);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Request failed';
          setError(message);
          setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(params ?? {}), tick]);

  return { data, loading, error, refetch };
}
